import type { DescribeErrorContext, UserFacingError } from './describe';

/**
 * The browser wallet flow's (V2, plan 2026-10-04 §3.5) typed errors, for the web presenter.
 *
 * The vendored InOut.Mobile engine (`src/snappycore`) throws its own classes, and its own presenter
 * (`src/snappycore/lib/errors/describe.ts`) holds the plain copy for every one of them: each Hyperliquid code, each
 * Relay and bridge failure, the backup errors. The web presenter hands those to it instead of guessing from their
 * text. That presenter (with `config/chains` and its copy tables) is loaded behind a dynamic `import()` so the V1
 * entry chunk never carries it: the V2 loaders start it together with the engine (`loadEnginePresenter`), and an
 * engine error can only exist once the engine has run. The few cases where the mobile wording is wrong in a browser
 * (a wallet locked by a missing key, not by a cancelled biometric prompt; an account with no agent) and the
 * `/v2/snappy` client's session errors (thrown in V1 sessions too, before any engine loads) have web copy here.
 *
 * Errors are matched by `name` (every class sets it), never `instanceof`: nothing here may import the engine.
 */

type Mapping = { title?: string; detail: string; action?: string };

type EnginePresenter = (
    error: unknown,
    context?: DescribeErrorContext,
) => UserFacingError;

/** Class names the vendored presenter knows (plus the web `/v2/snappy` client's two, same shape as the mobile one). */
const ENGINE_ERROR_NAMES: ReadonlySet<string> = new Set([
    'HlError',
    'RelayError',
    'BridgeError',
    'WalletLockedError',
    'BackupPasswordError',
    'RecoveryKeyFormatError',
    'BackupFormatError',
    'RecoveryKeyRequiredError',
    'BackupPolicyError',
    'SnappyApiError',
    'SnappySessionExpiredError',
]);

/**
 * The vendored `GENERIC` copy (`copy.common.ts`): names no server, since none of ours is in a V2 money or order path.
 * `engine.test.ts` pins it to the vendored constant.
 */
export const ENGINE_GENERIC: Mapping = {
    detail: 'Something went wrong and this did not complete.',
    action: 'Try again. If it keeps happening, contact support.',
};

/** The social session behind a `/v2/snappy` call is gone (the V2 banner's own words). */
const SESSION_ENDED: Mapping = {
    title: 'Session ended',
    detail: 'Your session ended.',
    action: 'Sign in again.',
};

/** The key record is not in this browser: the web never prompts, so this is never a cancelled unlock. */
const WALLET_LOCKED: Mapping = {
    title: 'Wallet locked',
    detail: 'Restore your Snappy Wallet first.',
};

/**
 * Hyperliquid does not know the signer. The web signs with the master key (no agent, plan §1), so that means the
 * account was never funded: the vendored `HL_COPY.MUST_DEPOSIT` (pinned by `engine.test.ts`), not its agent copy.
 */
const MUST_DEPOSIT: Mapping = {
    title: 'Deposit needed',
    detail: 'Your Hyperliquid account has no funds yet.',
    action: 'Deposit USDC to start trading.',
};

const NETWORK: Mapping = {
    title: 'Connection problem',
    detail: 'We could not reach the service.',
    action: 'Check your internet connection and try again.',
};

const RATE_LIMITED: Mapping = {
    title: 'Too many requests',
    detail: 'There were too many requests in a short time.',
    action: 'Please wait a minute and try again.',
};

const UNAVAILABLE: Mapping = {
    title: 'Service temporarily unavailable',
    detail: 'The service is temporarily unavailable.',
    action: 'Try again in a minute.',
};

interface NamedError {
    name: string;
    message?: unknown;
    code?: unknown;
    status?: unknown;
}

function engineErrorOf(error: unknown): NamedError | null {
    if (typeof error !== 'object' || error === null) return null;
    const name = (error as { name?: unknown }).name;
    return typeof name === 'string' && ENGINE_ERROR_NAMES.has(name)
        ? (error as NamedError)
        : null;
}

/** True for an error of the V2 engine or the `/v2/snappy` client (see {@link describeEngineError}). */
export function isEngineError(error: unknown): boolean {
    return engineErrorOf(error) !== null;
}

/** The web's own copy for an engine error the mobile wording gets wrong in a browser, or null. */
function webEngineCopy(error: unknown): Mapping | null {
    const engineError = engineErrorOf(error);
    if (!engineError) return null;
    const { name, code, status } = engineError;
    if (
        name === 'SnappySessionExpiredError' ||
        (name === 'SnappyApiError' &&
            status === 401 &&
            (code === null ||
                code === undefined ||
                code === 'NOT_AUTHENTICATED'))
    ) {
        return SESSION_ENDED;
    }
    if (name === 'WalletLockedError') return WALLET_LOCKED;
    if (name === 'HlError' && code === 'USER_CANCELLED') return WALLET_LOCKED;
    if (name === 'HlError' && code === 'AGENT_MISSING') return MUST_DEPOSIT;
    return null;
}

let enginePresenter: EnginePresenter | null = null;
let loading: Promise<void> | null = null;

/**
 * Loads the vendored presenter once per page (its own lazy chunk). The V2 loaders await it next to the engine, so it
 * is in place before the engine can throw; the web presenter also starts it the first time it meets an engine error.
 */
export async function loadEnginePresenter(): Promise<void> {
    loading ??= import('@snappy/core/lib/errors/describe').then(
        (module) => {
            enginePresenter = module.describeErrorForUser;
        },
        (error: unknown) => {
            // a chunk that failed to load (offline, a deploy replaced it): the next call tries again
            loading = null;
            console.warn(
                '[UserFacingError] the engine presenter did not load',
                error,
            );
        },
    );
    return loading;
}

/** Tests only: forgets the loaded presenter. */
export function resetEnginePresenterForTests(): void {
    enginePresenter = null;
    loading = null;
}

function presentMapping(
    mapping: Mapping,
    context?: DescribeErrorContext,
): UserFacingError {
    return {
        title:
            mapping.title ??
            (context?.operation
                ? `${context.operation} did not complete`
                : 'Something went wrong'),
        detail: mapping.detail,
        ...(mapping.action ? { action: mapping.action } : {}),
    };
}

/**
 * Before the vendored presenter has loaded (only an identity error from a V1 session gets here in practice): a
 * status-level answer for the `/v2/snappy` client, the V2 backend's own plain message for a Hyperliquid failure (it
 * replaces `HlError.message` with this same copy), else the generic copy. Never the error's text otherwise.
 */
function fallbackMapping(
    error: NamedError,
    isPlainUserSentence: (text: string) => boolean,
): Mapping {
    if (error.name === 'SnappyApiError') {
        if (error.status === 0 || error.code === 'NETWORK') return NETWORK;
        if (error.status === 429 || error.code === 'RATE_LIMITED') {
            return RATE_LIMITED;
        }
        if (typeof error.status === 'number' && error.status >= 500) {
            return UNAVAILABLE;
        }
        return ENGINE_GENERIC;
    }
    if (
        error.name === 'HlError' &&
        typeof error.message === 'string' &&
        isPlainUserSentence(error.message)
    ) {
        return { detail: error.message };
    }
    return ENGINE_GENERIC;
}

/**
 * The web copy for an engine error whose mobile wording is wrong in a browser (a locked wallet, an account without
 * funds, an ended session), or null. The V2 funds flows, which call the vendored presenter directly, ask this first.
 */
export function describeWebEngineError(
    error: unknown,
    context?: DescribeErrorContext,
): UserFacingError | null {
    const web = webEngineCopy(error);
    return web ? presentMapping(web, context) : null;
}

/**
 * Plain copy for an error of the V2 engine or the `/v2/snappy` client, or null for anything else (the web presenter
 * keeps its own mapping for those): the web copy above first, then the vendored presenter.
 */
export function describeEngineError(
    error: unknown,
    context: DescribeErrorContext | undefined,
    isPlainUserSentence: (text: string) => boolean,
): UserFacingError | null {
    const engineError = engineErrorOf(error);
    if (!engineError) return null;
    const web = describeWebEngineError(error, context);
    if (web) return web;
    if (enginePresenter) return enginePresenter(error, context);
    void loadEnginePresenter();
    return presentMapping(
        fallbackMapping(engineError, isPlainUserSentence),
        context,
    );
}
